/**
 * Carrier-minimum parcel dimensions — single SoT.
 *
 * Surfaced 2026-06-30: USPS Priority Mail International returns `status:"ERROR"`
 * with opaque `baseRate` text at `transactions.create` time when the parcel
 * is below USPS-intl floors. Shippo's `/shipments` (rate-quote) accepts the
 * shipment silently — the carrier's refusal only fires at label-buy. Two
 * gates close this:
 *
 *   A. Listing-publish-time (Layer 1, contract): the Zod schemas in
 *      `listing-create.ts` and `listing-patch.ts` tighten `parcel_*` from
 *      `.positive().nullish()` to `.min(MIN_*).nullish()`. New listings
 *      cannot save with sub-floor dimensions.
 *
 *   B. Rate-quote-time (Layer 2, domain): `lib/shipping/rates.ts` calls
 *      `validateParcelMinDims()` after normalising the rates request to
 *      canonical grams + millimetres. Grandfathered sub-floor listings are
 *      refused with `kind: "parcel_below_carrier_minimum"`.
 *
 * Why this lives in `@kraahl/contract`:
 * - Both web (`lib/shipping/rates.ts`) and the listing-create / listing-patch
 *   schemas need the same floor numbers. Mobile (`apps/mobile/lib/listing-create-api.ts`)
 *   imports `CreateListingRequest` from this package, so the tightened Zod
 *   schema applies to mobile automatically. The constants must live above
 *   both web and mobile to satisfy hex §1 (domain rules cross delivery
 *   shells without duplication).
 *
 * Floor values (operator-approved 2026-06-30, tightened 2026-07-03):
 *   length  ≥ 153 mm (6.02 in) — USPS strict 6-in floor (152.4 mm) rounded up
 *   width   ≥ 100 mm (~4 in)   — cross-carrier conservative
 *   height  ≥  25 mm (~1 in)   — accepts padded mailers, blocks postcards
 *   weight  ≥  30 g  (~1 oz)   — USPS-min weight class
 *
 * Length was 150 mm from 2026-06-30 to 2026-07-03. Empirically (live probe
 * against US→FR 227g/152×152×51mm) sub-152.4 mm listings passed publish + rate
 * quote but Shippo dropped USPS FCPIS silently (only PMI + PMEI came back) and
 * label-buy hit the same `baseRate` refusal HANDOFF_BASERATE_FR_2026_06_30.md
 * documented. Bumped to 153 (152.4 rounded up) so USPS strict floor is the
 * gate, not just IMM. Grandfathered listings fall through Option B in the route
 * with an axis-precise error message.
 *
 * One global floor is the pragmatic USPS-MVP scope. Per-service / per-carrier
 * floors earn their keep once non-USPS / sub-postcard carriers ship (PARCEL_MIN_DIMS.md
 * §6 question 2).
 *
 * @see .tondocs/shippo/PARCEL_MIN_DIMS.md — full incident write-up + research
 * @see .tondocs/shippo/HANDOFF_BASERATE_FR_2026_06_30.md — original handoff
 * @see lib/shipping/rates.ts — domain consumer (`fetchShippingRates`)
 * @see packages/contract/src/listing-create.ts — Option A consumer
 * @see packages/contract/src/listing-patch.ts — Option A consumer
 */

// ---------------------------------------------------------------------------
// Constants — single source of truth
// ---------------------------------------------------------------------------

/** Minimum parcel LENGTH in millimetres. USPS strict 6-in floor (152.4 mm) rounded up. */
export const MIN_PARCEL_LENGTH_MM = 153;

/** Minimum parcel WIDTH in millimetres. Cross-carrier conservative. */
export const MIN_PARCEL_WIDTH_MM = 100;

/** Minimum parcel HEIGHT in millimetres. Accepts padded mailers, blocks postcards. */
export const MIN_PARCEL_HEIGHT_MM = 25;

/** Minimum parcel WEIGHT in grams. ~1 oz, USPS-min weight class. */
export const MIN_PARCEL_WEIGHT_GRAMS = 30;

// ---------------------------------------------------------------------------
// Validator helper — used by the rate-quote-time gate (Option B)
// ---------------------------------------------------------------------------

/** Per-axis label used when reporting which dimension(s) violated the floor. */
export type ParcelMinDimsAxis = "length" | "width" | "height" | "weight";

/**
 * Canonical-unit parcel inputs for `validateParcelMinDims`. Caller MUST
 * normalise to grams + millimetres first (use `lib/shipping/normalize-parcel.ts`
 * `toGrams` / `toMm` on the rate-quote side). Naming mirrors the DB columns
 * so it cannot be confused with the legacy locale-shape (which carried unit
 * fields alongside).
 */
export interface CanonicalParcelForMinCheck {
  lengthMm: number;
  widthMm: number;
  heightMm: number;
  weightGrams: number;
}

/**
 * Floor payload — surfaced on the violation branch and on the route's error
 * `details.minimum`. Frozen shape: changing the keys is a wire-shape change.
 */
export interface ParcelMinDimsFloor {
  length: number;
  width: number;
  height: number;
  weight: number;
}

/**
 * Discriminated result of `validateParcelMinDims`. `ok: true` is the
 * pass-through; `ok: false` carries the floor + actual values so the
 * caller can render a precise operator/seller-facing message without
 * having to re-read the constants.
 */
export type ParcelMinDimsResult =
  | { ok: true }
  | {
      ok: false;
      minimum: ParcelMinDimsFloor;
      actual: ParcelMinDimsFloor;
      violations: readonly ParcelMinDimsAxis[];
    };

/** Frozen floor object — referenced from violation results so callers can
 *  surface "minimum required" without re-importing every constant. */
export const PARCEL_MIN_DIMS_FLOOR: ParcelMinDimsFloor = Object.freeze({
  length: MIN_PARCEL_LENGTH_MM,
  width: MIN_PARCEL_WIDTH_MM,
  height: MIN_PARCEL_HEIGHT_MM,
  weight: MIN_PARCEL_WEIGHT_GRAMS,
});

/**
 * Validate canonical-unit parcel against the global carrier-minimum floor.
 * Returns a discriminated result — `ok: true` for pass, otherwise the
 * specific violated axes + floor + actual values.
 *
 * Caller responsibility: normalise to canonical (`lib/shipping/normalize-parcel.ts`)
 * BEFORE calling — this helper does not own unit conversion.
 */
export function validateParcelMinDims(
  parcel: CanonicalParcelForMinCheck,
): ParcelMinDimsResult {
  const violations: ParcelMinDimsAxis[] = [];
  if (parcel.lengthMm < MIN_PARCEL_LENGTH_MM) violations.push("length");
  if (parcel.widthMm < MIN_PARCEL_WIDTH_MM) violations.push("width");
  if (parcel.heightMm < MIN_PARCEL_HEIGHT_MM) violations.push("height");
  if (parcel.weightGrams < MIN_PARCEL_WEIGHT_GRAMS) violations.push("weight");
  if (violations.length === 0) return { ok: true };
  return {
    ok: false,
    minimum: PARCEL_MIN_DIMS_FLOOR,
    actual: {
      length: parcel.lengthMm,
      width: parcel.widthMm,
      height: parcel.heightMm,
      weight: parcel.weightGrams,
    },
    violations,
  };
}
